import { useAuthStore } from "@features/auth/store/authStore";
import { MediaFile, MediaType } from "@shared/types/media.types";
import { FormEvent, useEffect, useRef, useState } from "react";
import ConfirmationModal from "../../../components/shared/ConfirmationModal";
import ModalBlank from "../../../ui/modals/ModalBlank";
import {
  PDFProcessingService,
  ProcessingStatus,
} from "../services/pdf-processing.service";
import { TestService } from "../services/test.service";

interface AddTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ProcessingStep = ({
  status,
  step,
  label,
  description,
}: {
  status: string;
  step: string;
  label: string;
  description: string;
}) => {
  const getStepStatus = () => {
    const processingOrder = [
      "uploading",
      "initializing",
      "loading",
      "analyzing",
      "converting",
      "processing_answers",
      "processing_content",
      "matching",
      "completed",
    ];

    const currentIndex = processingOrder.indexOf(status);
    const stepIndex = processingOrder.indexOf(step);

    if (currentIndex === stepIndex) return "active";
    if (currentIndex > stepIndex) return "completed";
    return "pending";
  };

  const stepStatus = getStepStatus();

  return (
    <div className="flex items-start">
      <div className="flex-shrink-0 mr-3">
        {stepStatus === "active" && (
          <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center animate-pulse">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        )}
        {stepStatus === "completed" && (
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
        {stepStatus === "pending" && (
          <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
        )}
      </div>
      <div>
        <h4
          className={`font-medium ${
            stepStatus === "active"
              ? "text-indigo-500"
              : stepStatus === "completed"
              ? "text-green-500"
              : "text-gray-500"
          }`}
        >
          {label}
        </h4>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  );
};

export const AddTestModal = ({
  isOpen,
  onClose,
  onSuccess,
}: AddTestModalProps) => {
  const { user } = useAuthStore();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [answersFile, setAnswersFile] = useState<File | null>(null);
  const [pdfStartPage, setPdfStartPage] = useState(1);
  const [pdfEndPage, setPdfEndPage] = useState(0);
  const [startPage, setStartPage] = useState(1);
  const [answerPages, setAnswerPages] = useState(1);
  const [alternativeProcessing, setAlternativeProcessing] = useState(false);
  const [currentFormat, setCurrentFormat] = useState(false);
  const [diagnosticTest, setDiagnosticTest] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStatus, setProcessingStatus] =
    useState<ProcessingStatus | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showNameConfirmation, setShowNameConfirmation] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const answersFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      processingStatus &&
      !["error", "completed"].includes(processingStatus.status)
    ) {
      setShowDetails(true);
    }

    // Auto-close modal when processing is completed
    if (processingStatus?.status === "completed") {
      // Close modal after a short delay to show success message
      const timer = setTimeout(() => {
        // Refresh the list
        onSuccess();
        onClose();
        setProcessingStatus(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [processingStatus?.status, onSuccess, onClose]);

  const checkTestNameAndSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !name.trim() || !user?.uid) return;

    setIsCheckingName(true);
    try {
      // Check if a test with the same name already exists
      const exists = await TestService.checkTestNameExists(
        user.uid,
        name.trim()
      );

      if (exists) {
        // If exists, show confirmation modal
        setShowNameConfirmation(true);
      } else {
        // If not exists, proceed with processing
        processTestFile();
      }
    } catch (error) {
      console.error("Error checking test name:", error);
      processTestFile();
    } finally {
      setIsCheckingName(false);
    }
  };

  const processTestFile = async () => {
    if (!file || !name.trim() || !user?.uid) return;

    setIsSubmitting(true);
    setShowDetails(true);
    setProcessingStatus({
      status: "uploading",
      progress: 0,
      message: "Starting upload...",
    });

    try {
      // Create media file object for main PDF
      const mediaFile: MediaFile = {
        file,
        preview: URL.createObjectURL(file),
        type: MediaType.IMAGE, // We'll treat PDF as image for simplicity
        progress: 0,
      };

      // Create media file object for answers PDF if provided
      let answersMediaFile: MediaFile | null = null;
      if (answersFile) {
        answersMediaFile = {
          file: answersFile,
          preview: URL.createObjectURL(answersFile),
          type: MediaType.IMAGE,
          progress: 0,
        };
      }

      // Process the PDF
      await PDFProcessingService.uploadAndProcessPDF(
        user.uid,
        mediaFile,
        name,
        {
          answerPages,
          startPage,
          alternativeProcessing,
          currentFormat,
          answersMediaFile,
          pdfStartPage,
          pdfEndPage,
          diagnosticTest,
        },
        (status) => {
          setProcessingStatus(status);
        }
      );

      // Reset form
      setName("");
      setFile(null);
      setAnswersFile(null);
      setStartPage(1);
      setAnswerPages(1);
      setAlternativeProcessing(false);
      setCurrentFormat(false);
      setPdfStartPage(1);
      setPdfEndPage(0);
      setDiagnosticTest(false);
    } catch (error) {
      console.error("Error processing PDF:", error);
      setProcessingStatus({
        status: "error",
        progress: 0,
        message: "Error processing PDF",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = checkTestNameAndSubmit;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        alert("Please select a PDF file");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleAnswersFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        alert("Please select a PDF file");
        return;
      }
      setAnswersFile(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    isAnswersFile = false
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (droppedFile.type !== "application/pdf") {
        alert("Please drop a PDF file");
        return;
      }
      if (isAnswersFile) {
        setAnswersFile(droppedFile);
      } else {
        setFile(droppedFile);
      }
    }
  };

  const handleReset = (isAnswersFile = false) => {
    if (isAnswersFile) {
      setAnswersFile(null);
      if (answersFileInputRef.current) {
        answersFileInputRef.current.value = "";
      }
    } else {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <ModalBlank id="add-test-modal" modalOpen={isOpen} setModalOpen={onClose}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Add New Test
            </h2>
            <button
              className="text-slate-400 hover:text-slate-500"
              onClick={onClose}
              disabled={isSubmitting}
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4 fill-current">
                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414 1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
              </svg>
            </button>
          </div>

          {processingStatus?.status === "error" ? (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-md">
              <div className="font-semibold">Error processing PDF</div>
              <div className="text-sm">{processingStatus.error}</div>
              <button
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
                onClick={() => setProcessingStatus(null)}
              >
                Try again
              </button>
            </div>
          ) : processingStatus?.status === "completed" ? (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-md">
              <div className="font-semibold">Success!</div>
              <div className="text-sm">{processingStatus.message}</div>
              {processingStatus.testId && (
                <div className="text-xs mt-1">
                  ID of the test: {processingStatus.testId}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                Closing automatically in a few seconds...
              </div>
            </div>
          ) : null}

          {processingStatus &&
            !["error", "completed"].includes(processingStatus.status) && (
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {processingStatus.message}
                  </span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {Math.round(processingStatus.progress)}%
                  </span>
                </div>
                <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${processingStatus.progress}%` }}
                  ></div>
                </div>

                {/* Detailed processing steps */}
                <div className="mt-4 space-y-2 text-sm">
                  <ProcessingStep
                    status={processingStatus.status}
                    step="uploading"
                    label="Uploading file"
                    description="Uploading PDF to Firebase Storage"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="initializing"
                    label="Initializing"
                    description="Preparing PDF processing"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="loading"
                    label="Loading PDF"
                    description="Loading PDF document"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="analyzing"
                    label="Analyzing structure"
                    description="Analyzing document structure"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="converting"
                    label="Processing pages"
                    description="Processing PDF pages"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="processing_answers"
                    label="Processing answers"
                    description="Extracting correct answers"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="processing_content"
                    label="Processing questions"
                    description="Extracting questions and options"
                  />
                  <ProcessingStep
                    status={processingStatus.status}
                    step="matching"
                    label="Matching data"
                    description="Matching questions with correct answers"
                  />
                </div>
              </div>
            )}

          {!processingStatus || processingStatus.status === "error" ? (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="test-name"
                  >
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="test-name"
                    className="form-input w-full"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter test name"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    PDF File <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      file
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e)}
                  >
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      disabled={isSubmitting}
                    />

                    {file ? (
                      <div className="py-2">
                        <div className="flex items-center justify-center mb-2">
                          <svg
                            className="w-8 h-8 text-indigo-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M14 3v4a2 2 0 002 2h4"
                            />
                          </svg>
                        </div>
                        <div className="text-sm font-medium text-indigo-500">
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        <button
                          type="button"
                          className="mt-2 text-xs text-red-500 hover:text-red-700"
                          onClick={() => handleReset()}
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        className="py-8 cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none"
                          >
                            <span>Upload a PDF file</span>
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PDF up to 50MB</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Main PDF Page Configuration
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-sm text-gray-600 mb-1"
                        htmlFor="pdf-start-page"
                      >
                        Start Page
                      </label>
                      <input
                        id="pdf-start-page"
                        className="form-input w-full"
                        type="number"
                        min="1"
                        value={pdfStartPage}
                        onChange={(e) =>
                          setPdfStartPage(parseInt(e.target.value) || 1)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm text-gray-600 mb-1"
                        htmlFor="pdf-end-page"
                      >
                        End Page
                      </label>
                      <input
                        id="pdf-end-page"
                        className="form-input w-full"
                        type="number"
                        min="0"
                        value={pdfEndPage}
                        onChange={(e) =>
                          setPdfEndPage(parseInt(e.target.value) || 0)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Configure which pages of the main PDF to process. Leave End
                    Page as 0 to process until the end.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Answers PDF File (Optional)
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      answersFile
                        ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, true)}
                  >
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleAnswersFileChange}
                      ref={answersFileInputRef}
                      disabled={isSubmitting}
                    />

                    {answersFile ? (
                      <div className="py-2">
                        <div className="flex items-center justify-center mb-2">
                          <svg
                            className="w-8 h-8 text-green-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M14 3v4a2 2 0 002 2h4"
                            />
                          </svg>
                        </div>
                        <div className="text-sm font-medium text-green-500">
                          {answersFile.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {(answersFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        <button
                          type="button"
                          className="mt-2 text-xs text-red-500 hover:text-red-700"
                          onClick={() => handleReset(true)}
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        className="py-6 cursor-pointer"
                        onClick={() => answersFileInputRef.current?.click()}
                      >
                        <svg
                          className="mx-auto h-10 w-10 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="answers-file-upload"
                            className="relative cursor-pointer rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none"
                          >
                            <span>Upload answers PDF</span>
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF with answer keys
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Answer Pages Configuration
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-sm text-gray-600 mb-1"
                        htmlFor="start-page"
                      >
                        Start Page
                      </label>
                      <input
                        id="start-page"
                        className="form-input w-full"
                        type="number"
                        min="1"
                        value={startPage}
                        onChange={(e) =>
                          setStartPage(parseInt(e.target.value) || 1)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm text-gray-600 mb-1"
                        htmlFor="answer-pages"
                      >
                        Number of Pages
                      </label>
                      <input
                        id="answer-pages"
                        className="form-input w-full"
                        type="number"
                        min="1"
                        max="10"
                        value={answerPages}
                        onChange={(e) =>
                          setAnswerPages(parseInt(e.target.value) || 1)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {answersFile
                      ? "Number of pages to process from the answers PDF"
                      : "Number of answer pages at the end of the test"}
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={alternativeProcessing}
                      onChange={(e) =>
                        setAlternativeProcessing(e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                    <span className="text-sm ml-2">
                      Use alternative processing method
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Try this if the standard processing method doesn't work well
                    with your PDF
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={currentFormat}
                      onChange={(e) => setCurrentFormat(e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span className="text-sm ml-2">Current Format</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Use this for PDFs in the current test format
                  </p>
                </div>

                {/* <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={diagnosticTest}
                      onChange={(e) =>
                        setDiagnosticTest(e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                    <span className="text-sm ml-2">
                      Diagnostic Test
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Mark this test as a diagnostic test
                  </p>
                </div> */}

                <div className="flex justify-end space-x-2 pt-3">
                  <button
                    type="button"
                    className="btn-sm border-gray-200 hover:border-gray-300 text-gray-600"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                    disabled={isSubmitting || !file || !name.trim()}
                  >
                    {isSubmitting ? "Processing..." : "Process PDF"}
                  </button>
                </div>
              </div>
            </form>
          ) : null}
        </div>
      </ModalBlank>
      {/* Confirmation modal for duplicate test name */}
      <ConfirmationModal
        isOpen={showNameConfirmation}
        onClose={() => setShowNameConfirmation(false)}
        onConfirm={async () => {
          setShowNameConfirmation(false);
          processTestFile();
        }}
        isLoading={isSubmitting}
        title="Duplicate Test Name"
        message={`A test with the name "${name}" already exists. Do you want to continue and replace it?`}
        confirmText="Replace"
        cancelText="Cancel"
        icon={
          <svg
            className="shrink-0 fill-current text-amber-500"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
          </svg>
        }
      />
    </>
  );
};
